import { ApolloQueryResult } from '@apollo/client';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { TDataType } from 'src/content/main/CustomReport/Newsletter/components/ModalNewsLetterDataSource';
import { IAdvanceSearchSocialMedia } from 'src/models/general';
import {
  FrequentyDelivery,
  IGetArticleV2,
  IGetNewsLetterAutomaticWithPagination,
  IGetNewsLetterWithPagination,
  INormalizedStream
} from 'src/models/newsLetter';
import {
  IArticleOfflineMediaPayload,
  IOfflineMediaPagination
} from 'src/models/offlineMedia';
import { IAdvanceSearchOnm } from 'src/models/onlineMedia';
import { IStreamOutputWithPagination } from 'src/models/stream';
import { IAdvanceSearchOfflineMedia } from './advanceSearchOfflineMedia';
import { initialState as initialStateOfm } from 'src/features/advanceSearchOfflineMedia';
import { initialState as initialStateOnm } from 'src/features/advanceSearchOnlineMedia';
import { initialState as initialStateSocialMedia } from 'src/features/advanceSearchSocialMedia';

export interface INewsLetter {
  id: string;
  name: string;
  deliveryType: string;
  scheduleTime: string;
  status: string;
  recipients: string[];
}

export type TActiveSection =
  | 'social_media'
  | 'online_media'
  | 'broadcast'
  | 'print_media'
  | '';

export interface IStateNewsLetter {
  newsLetterList: ApolloQueryResult<IGetNewsLetterWithPagination>;
  newsLetterAutomaticList: ApolloQueryResult<IGetNewsLetterAutomaticWithPagination>;
  activeSection: TActiveSection;
  selectedNewsLetter: {
    customizeTemplate: {
      //step 1 -- content setting
      titleColor?: string;
      isSourceIcon?: boolean;
      isSeedMoreButton?: boolean;
      readMoreLabel?: string;
      fontColor?: string;
      isLikeMetric?: boolean;
      isViewMetric?: boolean;
      isCommentMetric?: boolean;
      isEngagementMetric?: boolean;
      isSentimentMetric?: boolean;
      isPrValue?: boolean;
      isNumberOfKeywords?: boolean;
      isSentiment?: boolean;

      //step 1 -- content styles
      mainColor?: string;
      isLogo?: boolean;
      logoUrl?: string;
      isHeaderImage?: boolean;
      headerImageUrl?: string;
      isPrimaryText?: boolean;
      primaryText?: string;
      isSecondaryText?: boolean;
      secondaryText?: string;
      footerBackgroudColor?: string;
      footerText?: string;
      isFooterSocialMedia?: boolean;
      instagram?: string;
      facebook?: string;
      twitter?: string;
      tiktok?: string;
      youtube?: string;
    };
    selectDataSource: {
      //step 2
      startDate: Date | string;
      endDate: Date | string;
      dataSource: TDataType | '';
      objectIds: Array<string>;
      objectGroupId: string;
      clippingId: string;
      // otherMediaId: string;
      printMediaId: string;
      broadcastId: string;
      orderBy: string;
      sortBy: string;
      sourceType: string;
      selectedStreamSosmedList: Array<INormalizedStream>;
      selectedStreamOnlineMediaList: Array<INormalizedStream>;
      selectedStreamBroadcastList: Array<INormalizedStream>;
      selectedStreamPrintMediaList: Array<INormalizedStream>;
      filterDataSourceSosmed: IAdvanceSearchSocialMedia;
      filterDataSourcePrintMedia: IAdvanceSearchOfflineMedia;
      filterDataSourceBroadcast: IAdvanceSearchOfflineMedia;
      filterDataSourceOnm: IAdvanceSearchOnm;
    };
    emailSetting: {
      //step 3
      templateName: string;
      emailSubject?: string;
      recipient?: string[];
      metric: FrequentyDelivery | '';
      timezone?: number;
      deliveryDate?: Date | string;
      newsLetterStart?: string[];
    };
  };
  streamSocialMediaList: IStreamOutputWithPagination;
  streamOnlineMediaList: IGetArticleV2;
  streamOfflineMediaList: IOfflineMediaPagination;
  translatedStreams: INormalizedStream[];
  mediaType: string;
  recoverTimeframe: {
    startDate: Date | string;
    endDate: Date | string;
  };
}

export const initialSelectedNewsLetter: IStateNewsLetter['selectedNewsLetter'] =
  {
    customizeTemplate: {
      titleColor: '',
      isSourceIcon: false,
      isSeedMoreButton: false,
      readMoreLabel: '',
      fontColor: '',
      isLikeMetric: false,
      isViewMetric: false,
      isCommentMetric: false,
      isEngagementMetric: false,
      isSentimentMetric: false,
      isPrValue: false,
      isNumberOfKeywords: false,
      isSentiment: false,

      mainColor: '',
      isLogo: false,
      logoUrl: '',
      isHeaderImage: false,
      headerImageUrl: '',
      isPrimaryText: false,
      primaryText: '',
      isSecondaryText: false,
      secondaryText: '',
      footerBackgroudColor: '',
      footerText: '',
      isFooterSocialMedia: false,
      instagram: '',
      facebook: '',
      twitter: '',
      tiktok: '',
      youtube: ''
    },
    selectDataSource: {
      startDate: moment().subtract(7, 'days').toDate(),
      endDate: moment().toDate(),
      dataSource: '',
      objectIds: [],
      objectGroupId: '',
      clippingId: '',
      // otherMediaId: '',
      printMediaId: '',
      broadcastId: '',
      orderBy: 'desc',
      sortBy: 'dateColumn',
      sourceType: 'social_media',
      selectedStreamSosmedList: [],
      selectedStreamOnlineMediaList: [],
      selectedStreamBroadcastList: [],
      selectedStreamPrintMediaList: [],
      filterDataSourceSosmed: initialStateSocialMedia,
      filterDataSourcePrintMedia: initialStateOfm,
      filterDataSourceBroadcast: initialStateOfm,
      filterDataSourceOnm: initialStateOnm
    },
    emailSetting: {
      templateName: '',
      emailSubject: '',
      recipient: [],
      metric: '',
      timezone: 0,
      deliveryDate: '',
      newsLetterStart: []
    }
  };

export const initialState: IStateNewsLetter = {
  newsLetterList: {
    data: {
      page: 1,
      limit: 10,
      totalPages: 1,
      totalRows: 0,
      result: []
    },
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  newsLetterAutomaticList: {
    data: {
      page: 1,
      limit: 10,
      totalPages: 1,
      totalRows: 0,
      result: []
    },
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  activeSection: '',
  selectedNewsLetter: initialSelectedNewsLetter,
  streamSocialMediaList: {
    page: 1,
    limit: 100,
    totalPages: 1,
    totalRows: 0,
    result: []
  },
  streamOnlineMediaList: {
    page: 1,
    limit: 100,
    totalPages: 1,
    totalRows: 0,
    result: []
  },
  streamOfflineMediaList: {
    page: 1,
    limit: 100,
    totalPages: 1,
    totalRows: 0,
    result: []
  },
  translatedStreams: [],
  mediaType: '',
  recoverTimeframe: {
    startDate: null,
    endDate: null
  }
};

export const customNewsLetter = createSlice({
  name: 'custom-news-letter',
  initialState,
  reducers: {
    /**
     * Updates the newsLetterList in the state with the payload from the action.
     * The payload is expected to be an ApolloQueryResult containing an INewsLetterResponse.
     */
    reducerUpdateNewsLetterList(
      state: IStateNewsLetter,
      action: PayloadAction<IGetNewsLetterWithPagination>
    ) {
      state.newsLetterList.data = action.payload;
    },

    /**
     * Updates the selectedNewsLetter's customizeTemplate in the state with the payload from the action.
     * The payload is expected to be an IStateNewsLetter['selectedNewsLetter']['customizeTemplate'].
     */
    reducerUpdateDataCustomTemplate(
      state: IStateNewsLetter,
      action: PayloadAction<
        IStateNewsLetter['selectedNewsLetter']['customizeTemplate']
      >
    ) {
      state.selectedNewsLetter.customizeTemplate = action.payload;
    },

    /**
     * Updates the selectedNewsLetter's selectDataSource in the state with the payload from the action.
     * The payload is expected to be an IStateNewsLetter['selectedNewsLetter']['selectDataSource'].
     */
    reducerUpdateDataSelectDataSource(
      state: IStateNewsLetter,
      action: PayloadAction<
        IStateNewsLetter['selectedNewsLetter']['selectDataSource']
      >
    ) {
      state.selectedNewsLetter.selectDataSource = action.payload;
    },

    /**
     * Updates the selectedNewsLetter's emailSetting in the state with the payload from the action.
     * The payload is expected to be an IStateNewsLetter['selectedNewsLetter']['emailSetting'].
     */
    reducerUpdateDataEmailSetting(
      state: IStateNewsLetter,
      action: PayloadAction<
        IStateNewsLetter['selectedNewsLetter']['emailSetting']
      >
    ) {
      state.selectedNewsLetter.emailSetting = action.payload;
    },
    /**
     * Updates the streamSocialMediaList in the state with the payload from the action.
     * The payload is expected to be an ApolloQueryResult containing an IStreamOutputWithPagination.
     */
    reducerUpdateStreamSosmedList(
      state: IStateNewsLetter,
      action: PayloadAction<IStreamOutputWithPagination>
    ) {
      state.streamSocialMediaList = action.payload;
    },
    reducerUpdateStreamOnlineMediaList(
      state: IStateNewsLetter,
      action: PayloadAction<IGetArticleV2>
    ) {
      state.streamOnlineMediaList = action.payload;
    },
    reducerUpdateStreamOfflineMediaList(
      state: IStateNewsLetter,
      action: PayloadAction<IOfflineMediaPagination>
    ) {
      state.streamOfflineMediaList = action.payload;
    },

    /**
     * Updates the activeSection in the state with the payload from the action.
     * The payload is expected to be a string.
     */
    reducerUpdateActiveSection(
      state: IStateNewsLetter,
      action: PayloadAction<TActiveSection>
    ) {
      state.activeSection = action.payload;

      switch (action.payload) {
        case 'social_media':
          state.selectedNewsLetter.selectDataSource.dataSource = 'object';
          state.selectedNewsLetter.selectDataSource.sourceType = 'social_media';
          break;
        case 'online_media':
          state.selectedNewsLetter.selectDataSource.dataSource = 'clipping';
          state.selectedNewsLetter.selectDataSource.sourceType = 'online_media';
          break;
        case 'broadcast':
          state.selectedNewsLetter.selectDataSource.dataSource = 'broadcast';
          state.selectedNewsLetter.selectDataSource.sourceType =
            'offline_media';
          break;
        case 'print_media':
          state.selectedNewsLetter.selectDataSource.dataSource = 'print_media';
          state.selectedNewsLetter.selectDataSource.sourceType =
            'offline_media';
          break;
        default:
          break;
      }
    },

    /**
     * Updates the objectIds for the selected news letter's data source.
     * The payload is expected to be an array of strings.
     */
    reducerUpdateObjectIdList(
      state: IStateNewsLetter,
      action: PayloadAction<string[]>
    ) {
      state.selectedNewsLetter.selectDataSource.objectIds = action.payload;
    },

    /*************  ✨ Windsurf Command ⭐  *************/
    /**
     * Updates the start and end dates for the selected news letter's data source.
     * @param {IStateNewsLetter} state - The state of the custom news letter feature.
     * @param {PayloadAction<{startDate: Date, endDate: Date}>} action - The action containing the new start and end dates.
     */
    /*******  dc42a4dd-167c-44af-8738-1e3e200d9495  *******/
    reducerUpdateTimeframe(
      state: IStateNewsLetter,
      action: PayloadAction<{ startDate: Date; endDate: Date }>
    ) {
      state.selectedNewsLetter.selectDataSource.startDate =
        action.payload.startDate;
      state.selectedNewsLetter.selectDataSource.endDate =
        action.payload.endDate;
    },
    reducerResetSelectedNewsLetter(
      state: IStateNewsLetter,
      action: PayloadAction<IStateNewsLetter['selectedNewsLetter']>
    ) {
      state.selectedNewsLetter = action.payload;
      state.activeSection = '';
      state.streamSocialMediaList = {
        page: 1,
        limit: 100,
        totalPages: 1,
        totalRows: 0,
        result: []
      };
      state.streamOnlineMediaList = {
        page: 1,
        limit: 100,
        totalPages: 1,
        totalRows: 0,
        result: []
      };
      state.streamOfflineMediaList = {
        page: 1,
        limit: 100,
        totalPages: 1,
        totalRows: 0,
        result: []
      };
    },
    reducerUpdateFilterDataSourceSosmed(
      state: IStateNewsLetter,
      action: PayloadAction<
        IStateNewsLetter['selectedNewsLetter']['selectDataSource']['filterDataSourceSosmed']
      >
    ) {
      state.selectedNewsLetter.selectDataSource.filterDataSourceSosmed =
        action.payload;
    },
    reducerUpdateFilterDataSourcePrintMedia(
      state: IStateNewsLetter,
      action: PayloadAction<
        IStateNewsLetter['selectedNewsLetter']['selectDataSource']['filterDataSourcePrintMedia']
      >
    ) {
      state.selectedNewsLetter.selectDataSource.filterDataSourcePrintMedia =
        action.payload;
    },
    reducerUpdateFilterDataSourceOnlineMedia(
      state: IStateNewsLetter,
      action: PayloadAction<
        IStateNewsLetter['selectedNewsLetter']['selectDataSource']['filterDataSourceOnm']
      >
    ) {
      state.selectedNewsLetter.selectDataSource.filterDataSourceOnm =
        action.payload;
    },
    reducerUpdateFilterDataSourceBroadcast(
      state: IStateNewsLetter,
      action: PayloadAction<
        IStateNewsLetter['selectedNewsLetter']['selectDataSource']['filterDataSourceBroadcast']
      >
    ) {
      state.selectedNewsLetter.selectDataSource.filterDataSourceBroadcast =
        action.payload;
    },
    reducerUpdateNewsLetterAutomaticList(
      state: IStateNewsLetter,
      action: PayloadAction<IGetNewsLetterAutomaticWithPagination>
    ) {
      state.newsLetterAutomaticList.data = action.payload;
    },
    reducerUpdateMediaType(
      state: IStateNewsLetter,
      action: PayloadAction<string>
    ) {
      state.mediaType = action.payload;
    },
    reducerUpdateTranslatedStreams(
      state: IStateNewsLetter,
      action: PayloadAction<INormalizedStream>
    ) {
      const isStreamExist = state.translatedStreams.find(
        (item) => item.originalId === action.payload.originalId
      );
      state.translatedStreams = isStreamExist
        ? state.translatedStreams.map((value) => {
            return {
              ...value,
              title:
                value.originalId === action.payload.originalId
                  ? action.payload.title
                  : value.title,
              content:
                value.originalId === action.payload.originalId
                  ? action.payload.content
                  : value.content
            };
          })
        : [...state.translatedStreams, action.payload];
    },
    reducerRemoveTranslatedStreams(
      state: IStateNewsLetter,
      action: PayloadAction<INormalizedStream>
    ) {
      const excludedStreams = state.translatedStreams.filter(
        (item) => item.originalId !== action.payload.originalId
      );
      state.translatedStreams = excludedStreams;
    },
    reducerUpdateRecoverTimeframe(
      state: IStateNewsLetter,
      action: PayloadAction<{ startDate: Date; endDate: Date }>
    ) {
      state.recoverTimeframe.startDate = action.payload.startDate;
      state.recoverTimeframe.endDate = action.payload.endDate;
    }
  }
});

export const {
  reducerUpdateNewsLetterList,
  reducerUpdateDataCustomTemplate,
  reducerUpdateDataSelectDataSource,
  reducerUpdateDataEmailSetting,
  reducerUpdateStreamSosmedList,
  reducerUpdateActiveSection,
  reducerUpdateObjectIdList,
  reducerUpdateStreamOnlineMediaList,
  reducerUpdateTimeframe,
  reducerUpdateStreamOfflineMediaList,
  reducerResetSelectedNewsLetter,
  reducerUpdateFilterDataSourceSosmed,
  reducerUpdateFilterDataSourceBroadcast,
  reducerUpdateFilterDataSourceOnlineMedia,
  reducerUpdateFilterDataSourcePrintMedia,
  reducerUpdateNewsLetterAutomaticList,
  reducerUpdateTranslatedStreams,
  reducerRemoveTranslatedStreams,
  reducerUpdateMediaType,
  reducerUpdateRecoverTimeframe
} = customNewsLetter.actions;

export default customNewsLetter.reducer;
